/**
 * Thin wrappers around `window.chatbase` methods.
 *
 * Each function is safe to call before the widget has loaded — the queue proxy
 * installed by the bootstrap layer will buffer the calls.
 */

import type { ChatbaseEventHandler, CustomActionMap } from "../types";

function cb() {
  if (typeof window === "undefined" || !window.chatbase) return null;
  return window.chatbase;
}

/** Open the chat widget. */
export function openChat(): void {
  cb()?.("open");
}

/** Close the chat widget. */
export function closeChat(): void {
  cb()?.("close");
}

/** Reset the chat session (clears history). */
export function resetChat(): void {
  cb()?.("resetChat");
}

/** Set initial greeting messages displayed in the widget. */
export function setInitialMessages(messages: string[]): void {
  cb()?.("setInitialMessages", messages);
}

/** Register event listener on the Chatbase widget. */
export function addEventListener(event: string, handler: ChatbaseEventHandler): void {
  cb()?.("addEventListener", event, handler);
}

/** Remove a previously registered event listener. */
export function removeEventListener(event: string, handler: ChatbaseEventHandler): void {
  cb()?.("removeEventListener", event, handler);
}

/** Register client-side custom action handlers. */
export function registerTools(tools: CustomActionMap): void {
  cb()?.("registerTools", tools);
}

/** Identify the current user (identity verification). */
export function identify(payload: Record<string, unknown>): void {
  cb()?.("identify", payload);
}

/** Clear the current user identity. */
export function resetUser(): void {
  cb()?.("resetUser");
}

/** Returns true if `window.chatbase` reports state === "initialized". */
export function isWidgetInitialized(): boolean {
  try {
    return cb()?.("getState") === "initialized";
  } catch {
    return false;
  }
}
